import { DistributionStats } from "features/distributions/DistributionStats";
import { DistributionTiersBreakdown } from "features/distributions/DistributionTiersBreakdown";
import { useWaterfallDistributionTable } from "features/waterfall/WaterfallFundLevelData";
import { useWaterfallBreakdownRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { useInvestorEntityRetrieveSuspense } from "gen/endpoints/investor-entity/investor-entity";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const WaterfallReviewInvestorLevelPage: React.FC = () => {
  const { t } = useTranslation(["common", "waterfall"]);

  const {
    fundFamilyEventId,
    fundFamilyId,
    fundFamilySubEventId,
    fundEntityId,
    investorEntityId,
  } = useRequiredStringParams();

  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  const { data } = useWaterfallBreakdownRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
    fundFamilySubEventId,
    fundEntityId,
    {
      investor_entity_id: investorEntityId,
      is_equalized_view: isEqualizedView || undefined,
    },
  );

  const { data: investorEntityData } =
    useInvestorEntityRetrieveSuspense(investorEntityId);

  const tableProps = useWaterfallDistributionTable({
    fundFamilyId,
    fundFamilyEventId,
    fundFamilySubEventId,
    fundEntityId,
    data,
    investorEntityId,
  });

  return (
    <PageContent>
      <PageHeader
        title={t("waterfall:review.investorLevel.title")}
        backButton={{
          title: t("waterfall:review.investorTableLevel.title"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({})
            .capitalActivityEvent({ fundFamilyEventId })
            .waterfall({})
            .review({ fundFamilySubEventId, fundEntityId })
            .investorBreakdown({}).$,
        }}
        badge={{
          type: "default",
          text: investorEntityData.legal_name,
        }}
        stats={
          <DistributionStats
            data={data}
            isEqualizedView={isEqualizedView}
            setIsEqualizedView={setIsEqualizedView}
            showAdjustmentsButton={false}
          />
        }
      />
      <PageBody>
        <TableBox isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("waterfall:review.investorLevel.tableTitle")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable {...tableProps} />
          </TableBoxBody>
        </TableBox>
        <DistributionTiersBreakdown data={data} />
      </PageBody>
    </PageContent>
  );
};
